import Queue from '@sravni/utils/lib/queue';
import getConfig from 'next/config';
import { useEffect, useMemo, useRef } from 'react';

import { useAppSelector } from 'shared/lib';

import { accountSelector } from 'entities/user';

import { SENTRY_IGNORE_ERRORS, SENTRY_TRACES_SAMPLE_RATE } from '../config/SentryUserSync.config';

const sentryQueue = new Queue({ debug: false });

export const SentryUserSync = () => {
  const account = useAppSelector(accountSelector);
  const accountRef = useRef(account);
  const isInitializedRef = useRef(false);
  const { publicRuntimeConfig } = getConfig();

  const isEnabled = publicRuntimeConfig.environment === 'production';

  const sentryConfig = useMemo(
    () => ({
      tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
      dsn: publicRuntimeConfig.SENTRY_DSN,
      release: publicRuntimeConfig.release,
      environment: publicRuntimeConfig.SENTRY_ENVIRONMENT,
      ignoreErrors: SENTRY_IGNORE_ERRORS,
    }),
    [publicRuntimeConfig.SENTRY_DSN, publicRuntimeConfig.SENTRY_ENVIRONMENT, publicRuntimeConfig.release],
  );

  useEffect(() => {
    accountRef.current = account;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.sub, account?.email]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (isInitializedRef.current) {
      return;
    }

    sentryQueue.push(() => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const Sentry = require('@sentry/nextjs');

      if (isInitializedRef.current) {
        return;
      }

      Sentry.init(sentryConfig);
      isInitializedRef.current = true;

      const { current } = accountRef;
      if (current) {
        Sentry.setUser({ id: current.sub, email: current.email });
      } else {
        Sentry.setUser(null);
      }
    });
  }, [isEnabled, sentryConfig]);

  useEffect(() => {
    if (!isEnabled || !isInitializedRef.current) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Sentry = require('@sentry/nextjs');

    if (!account) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({ id: account.sub, email: account.email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.sub, account?.email, isEnabled]);

  return null;
};
